import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LanguageProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import BlogPage from "@/pages/BlogPage";
import AboutPage from "@/pages/AboutPage";
import BegleitungPage from "@/pages/BegleitungPage";
import StilPage from "@/pages/StilPage";
import PricingPage from "@/pages/PricingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ImprintPage from "@/pages/ImprintPage";
import ModulesPage from "@/pages/ModulesPage";
import BookingPage from "@/pages/BookingPage";
import AdminCalendarPage from "@/pages/AdminCalendarPage";
import MethodPage from "@/pages/MethodPage";
import CoachingInMotionPage from "@/pages/CoachingInMotionPage";
import GuidePage from "@/pages/GuidePage";
import DashboardPage from "@/pages/DashboardPage";
import TrackingPage from "@/pages/TrackingPage";
import ChatPage from "@/pages/ChatPage";
import ContentPage from "@/pages/ContentPage";
import ProgressPage from "@/pages/ProgressPage";
import ProfilePage from "@/pages/ProfilePage";
import HashimotoModule from "@/pages/HashimotoModule";
import CycleModule from "@/pages/CycleModule";
import MenopauseModule from "@/pages/MenopauseModule";
import GlowModule from "@/pages/GlowModule";
import DashboardLayout from "@/components/layout/DashboardLayout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="animate-pulse font-display text-2xl text-sage">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!user.onboarding_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Synchronous session_id detection (before useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/begleitung" element={<BegleitungPage />} />
      <Route path="/stil" element={<StilPage />} />
      <Route path="/methode" element={<MethodPage />} />
      <Route path="/coaching-in-motion" element={<CoachingInMotionPage />} />
      <Route path="/guide" element={<ProtectedRoute><DashboardLayout><GuidePage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/imprint" element={<ImprintPage />} />
      <Route path="/modules" element={<ModulesPage />} />
      <Route path="/booking" element={<ProtectedRoute><DashboardLayout><BookingPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/calendar" element={<ProtectedRoute><DashboardLayout><AdminCalendarPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/tracking" element={<ProtectedRoute><DashboardLayout><TrackingPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><DashboardLayout><ChatPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/content" element={<ProtectedRoute><DashboardLayout><ContentPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><DashboardLayout><ProgressPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><DashboardLayout><ProfilePage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/modules/hashimoto" element={<ProtectedRoute><DashboardLayout><HashimotoModule /></DashboardLayout></ProtectedRoute>} />
      <Route path="/modules/cycle" element={<ProtectedRoute><DashboardLayout><CycleModule /></DashboardLayout></ProtectedRoute>} />
      <Route path="/modules/menopause" element={<ProtectedRoute><DashboardLayout><MenopauseModule /></DashboardLayout></ProtectedRoute>} />
      <Route path="/modules/glow" element={<ProtectedRoute><DashboardLayout><GlowModule /></DashboardLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
